import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { useForm } from 'react-hook-form'
import * as z from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card'
import { useToast } from '@/lib/hooks/use-toast'
import PasswordInput from '@/components/form/input/password-input'
import { getCleanDomain } from '@/utils/getCleanDomain'
import domainConfigs from '@/config/domain'
import { ROUTES } from '@/config/routes'
import {
  hasSessionExpiredNotice,
  readSessionExpiredUntil,
} from '@/features/auth/utils/session-expired-notice'

import useSignIn from '../services/useSignIn'

const domain = getCleanDomain()
const domainConfig = domainConfigs[domain] || domainConfigs.default

const loginSchema = z.object({
  email: z.string().min(1, { message: 'Email é obrigatório' }).email({ message: 'Email inválido' }),
  password: z.string().min(1, { message: 'Senha é obrigatória' }),
  rememberMe: z.boolean().optional()
})
type LoginFormValues = z.infer<typeof loginSchema>

export default function LoginForm() {
  const { signIn } = useSignIn()
  const navigate = useNavigate()
  const { toast } = useToast()
  const [showSessionExpiredNotice, setShowSessionExpiredNotice] = useState(() => hasSessionExpiredNotice())

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false
    },
    mode: 'onChange' // Enable validation on change
  })

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'email' && value.email && value.email.includes('@') && !value.email.includes('@' + domain)) {
        const [localPart, currentDomain] = value.email.split('@')
        if (domain.includes('localhost')) {
          return
        }

        if (!currentDomain) {
          const emailWithDomain = `${localPart}@${domain}`
          form.setValue('email', emailWithDomain, { shouldValidate: false })
        }
      }
    })
    return () => subscription.unsubscribe()
  }, [form, domain])

  useEffect(() => {
    const hasStoredNotice = hasSessionExpiredNotice()
    if (!hasStoredNotice) return

    const visibleUntil = readSessionExpiredUntil()
    setShowSessionExpiredNotice(true)

    toast({
      description: 'Sua sessao expirou. Faca login novamente.',
      variant: 'destructive'
    })

    const remainingMs = Math.max(0, visibleUntil - Date.now())
    const timeoutId = window.setTimeout(() => setShowSessionExpiredNotice(false), remainingMs)

    return () => window.clearTimeout(timeoutId)
  }, [toast])

  const onSubmit = async (data: LoginFormValues) => {
    try {
      const user = await signIn(data.email, data.password)

      if (data.rememberMe) {
        localStorage.setItem('rememberedEmail', data.email)
      } else {
        localStorage.removeItem('rememberedEmail')
      }

      if (user.typeUser === 'ADMIN') {
        navigate(ROUTES.ADMIN_HOME.path)
      } else {
        navigate(ROUTES.DASHBOARD_HOME.path)
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast({
          description: error.response?.data?.message || 'Erro de rede',
          variant: 'destructive'
        })
      } else if (error instanceof Error) {
        toast({
          description: error.message || 'Erro de rede',
          variant: 'destructive'
        })
      } else {
        toast({
          description: 'Ocorreu um erro desconhecido',
          variant: 'destructive'
        })
      }
    }
  }

  const handleForgotPassword = () => {
    navigate(ROUTES.FORGOT_PASSWORD.path)
  }

  return (
    <Card className="relative z-10 w-full max-w-md bg-input dark:bg-input backdrop-blur-sm shadow-xl border-border">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <CardHeader className="space-y-1">
            <CardTitle className="text-3xl font-bold text-center text-primary dark:text-primary-hover">Login</CardTitle>
            <CardDescription className="text-center text-foreground">
              Coloque seu e-mail e senha para entrar
            </CardDescription>
          </CardHeader>
          {domainConfig.icon && (
            <div className="absolute inset-0 flex justify-center items-center opacity-10 pointer-events-none">
              <img src={domainConfig.icon} alt={`${domainConfig.title} Logo`} className="h-64 w-auto" />
            </div>
          )}
          <CardContent className="space-y-4">
            {showSessionExpiredNotice && (
              <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                Sua sessao expirou. Faca login novamente.
              </div>
            )}

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-foreground">E-mail</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="seu@email.com"
                      type="email"
                      className="bg-input dark:bg-input text-foreground"
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-foreground">Senha</FormLabel>
                  <FormControl>
                    <PasswordInput
                      field={field}
                      placeholder="Digite sua senha"
                      autoComplete="current-password"
                      transparentBackground={true} // Ativar fundo transparente
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="rememberMe"
              render={({ field }) => (
                <FormItem className="flex items-center gap-2">
                  <FormControl>
                    <Checkbox
                      id="rememberMe"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      className="border-primary text-primary"
                    />
                  </FormControl>
                  Lembre-me
                </FormItem>
              )}
            />
          </CardContent>

          <CardFooter className="flex flex-col space-y-2">
            <Button
              type="submit"
              className="w-full bg-primary hover:bg-primary-hover text-white"
              disabled={!form.formState.isValid}
            >
              Entrar
            </Button>
            <Button
              type="button"
              className="w-full text-primary hover:text-primary-hover"
              variant="link"
              onClick={handleForgotPassword}
            >
              Esqueci a senha
            </Button>
          </CardFooter>
        </form>
      </Form>
    </Card>
  )
}
